import React, { useState, useMemo, useContext } from 'react';
import axios from 'axios';
import withAuth from '../../common/auth';
import { format } from 'date-fns';
import { ToastContainer, toast } from 'react-toastify';
import { Slide } from 'react-toastify';
import { FiRefreshCcw, FiDownloadCloud, FiSearch, FiAlertCircle } from 'react-icons/fi';
import { BASE_URL, GET_EXPIRED_PRODUCT_DETAILS } from '../../utlilities/config';
import { ReportEmbedContext } from '../reportEmbedContext';

const Spinner = () => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
    <div className="flex flex-col items-center gap-3">
      <div className="h-16 w-16 animate-spin rounded-full border-b-4 border-t-4 border-indigo-600"></div>
      <p className="font-medium text-indigo-600">Loading...</p>
    </div>
  </div>
);

const formatDateDisplay = (value) => {
  if (!value) return '-';
  try {
    return format(new Date(value), 'dd-MM-yyyy');
  } catch (error) {
    return value;
  }
};

const formatExpiryMonthYear = (value) => {
  if (!value) return '-';
  try {
    return format(new Date(value), 'MM-yyyy');
  } catch (error) {
    return value;
  }
};

const toNumber = (value) => {
  const parsed = parseFloat(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const downloadCsv = (rows, fileName) => {
  const headers = [
    'Item No',
    'Item Name',
    'Batch Code',
    'Expiry Date',
    'On Hand',
    'Unit',
    'Purchase Date',
    'Purchase Ref No',
    'Supplier Name',
  ];

  const escape = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
  const csvRows = rows.map((row) =>
    [
      row.Item_No,
      row.Item_Name,
      row.BatchCode,
      formatDateDisplay(row.ExpiryDate),
      toNumber(row.Onhand),
      row.Unit || '',
      formatDateDisplay(row.PurchaseDate),
      row.PurchaseRefNo || '',
      row.SupplierName || '',
    ]
      .map(escape)
      .join(',')
  );

  const csv = [headers.join(','), ...csvRows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

function ExpiryReport() {
  const embedded = useContext(ReportEmbedContext);
  const token = localStorage.getItem('accessToken');
  const departmentId = localStorage.getItem('department_id') || '1';

  const [loading, setLoading] = useState(false);
  const [asOfDate, setAsOfDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [searchText, setSearchText] = useState('');
  const [rows, setRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 500;

  const filteredRows = useMemo(() => {
    if (!searchText.trim()) return rows;
    const query = searchText.toLowerCase();
    return rows.filter((row) => {
      return (
        String(row.Item_No || '').toLowerCase().includes(query) ||
        String(row.Item_Name || '').toLowerCase().includes(query) ||
        String(row.BatchCode || '').toLowerCase().includes(query) ||
        String(row.SupplierName || '').toLowerCase().includes(query) ||
        String(row.PurchaseRefNo || '').toLowerCase().includes(query)
      );
    });
  }, [rows, searchText]);

  const summary = useMemo(() => {
    const totalStock = filteredRows.reduce((acc, item) => acc + toNumber(item.Onhand), 0);
    const uniqueItems = new Set(filteredRows.map((item) => item.Item_No)).size;
    const zeroStockCount = filteredRows.filter((item) => toNumber(item.Onhand) === 0).length;
    return {
      totalRows: filteredRows.length,
      totalStock,
      uniqueItems,
      zeroStockCount,
    };
  }, [filteredRows]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / rowsPerPage));
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedRows = filteredRows.slice(startIndex, endIndex);

  const getExpiryData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}${GET_EXPIRED_PRODUCT_DETAILS}`, {
        headers: {
          XApiKey: process.env.REACT_APP_API_KEY,
          Authorization: `Bearer ${token}`,
          departmentID: departmentId,
          asOfDate: asOfDate,
        },
      });

      if (response.status === 200 && response.data?.isSucess) {
        setRows(response.data?.data || []);
        setCurrentPage(1);
      } else {
        toast.error(response.data?.message || 'Failed to fetch expiry report', { autoClose: 1500 });
        setRows([]);
      }
    } catch (error) {
      console.error('Failed to fetch expiry report:', error);
      toast.error('Failed to fetch expiry report', {
        icon: <FiAlertCircle className="text-red-500" />,
        autoClose: 1500,
      });
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (!rows.length) {
      toast.error('No data to export', { autoClose: 1200 });
      return;
    }
    const fileName = `Expiry_Report_${asOfDate}.csv`;
    // Export full fetched dataset (all rows), independent of pagination.
    downloadCsv(rows, fileName);
    toast.success('CSV exported successfully', { autoClose: 1200 });
  };

  return (
    <div className="flex h-full bg-background">
      <ToastContainer
        position="top-center"
        autoClose={1000}
        limit={1}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Slide}
      />

      {loading && <Spinner />}

      <div className="flex h-full flex-1 flex-col overflow-hidden">
        <header className="mb-2 shrink-0 border-b border-slate-200 bg-white px-3 py-2.5">
          <div className={`flex flex-col gap-3 ${embedded ? 'lg:flex-row lg:justify-end' : 'lg:flex-row lg:items-center lg:justify-between'}`}>
            {!embedded && (
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-widest text-indigo-500">Stock Reports</p>
                <h1 className="text-lg font-bold text-slate-800">Expiry Report</h1>
              </div>
            )}
            <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
              <button
                type="button"
                onClick={getExpiryData}
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2 text-sm font-semibold text-black shadow-sm transition hover:border-indigo-300 hover:text-indigo-500"
              >
                <FiRefreshCcw size={16} />
                Generate
              </button>
              <button
                type="button"
                onClick={handleExport}
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2 text-sm font-semibold text-black shadow-sm transition hover:border-indigo-300 hover:text-indigo-500"
              >
                <FiDownloadCloud size={16} />
                Export
              </button>
            </div>
          </div>
        </header>

        <main className="flex flex-1 flex-col overflow-hidden">
          <div className="flex min-h-0 flex-1 flex-col gap-4">
            <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="grid gap-4 md:grid-cols-3">
                <label className="flex flex-col gap-2 text-sm font-medium text-slate-700">
                  Up To Date
                  <input
                    type="date"
                    value={asOfDate}
                    onChange={(event) => setAsOfDate(event.target.value)}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  />
                </label>

                <label className="flex flex-col gap-2 text-sm font-medium text-slate-700 md:col-span-2">
                  Search
                  <div className="relative">
                    <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                    <input
                      type="text"
                      value={searchText}
                      onChange={(event) => {
                        setSearchText(event.target.value);
                        setCurrentPage(1);
                      }}
                      placeholder="Search by item, batch, supplier, ref no..."
                      className="w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm text-slate-700 shadow-sm focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                    />
                  </div>
                </label>
              </div>
            </section>
            {/* 
            <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="grid gap-4 md:grid-cols-4">
                <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-4 shadow-sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">Total Rows</p>
                  <p className="mt-1 text-2xl font-bold text-indigo-600">{summary.totalRows}</p>
                </div>
                <div className="rounded-xl border border-emerald-100 bg-emerald-50/60 p-4 shadow-sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-emerald-500">Total On Hand</p>
                  <p className="mt-1 text-2xl font-bold text-emerald-600">{summary.totalStock.toLocaleString()}</p>
                </div>
                <div className="rounded-xl border border-blue-100 bg-blue-50/60 p-4 shadow-sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-blue-500">Unique Items</p>
                  <p className="mt-1 text-2xl font-bold text-blue-600">{summary.uniqueItems}</p>
                </div>
                <div className="rounded-xl border border-amber-100 bg-amber-50/60 p-4 shadow-sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-amber-500">Zero Stock Rows</p>
                  <p className="mt-1 text-2xl font-bold text-amber-600">{summary.zeroStockCount}</p>
                </div>
              </div>
            </section> */}

            <section className="flex min-h-0 flex-1 flex-col rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <p className="text-sm text-slate-600">
                  Showing{' '}
                  <span className="font-semibold text-black">
                    {filteredRows.length === 0 ? 0 : startIndex + 1}-{Math.min(endIndex, filteredRows.length)}
                  </span>{' '}
                  of <span className="font-semibold text-black">{filteredRows.length}</span> records
                </p>
                {filteredRows.length > rowsPerPage && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:border-indigo-300 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-slate-600">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:border-indigo-300 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>

              <div className="min-h-0 flex-1 overflow-auto rounded-xl border border-slate-200">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="sticky top-0 z-10 bg-slate-50">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">S.No</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Item Name</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Batch</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Expiry Date</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">On Hand</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Purchase Date</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Purchase Ref No</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Supplier</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {filteredRows.length > 0 ? (
                      paginatedRows.map((row, index) => (
                        <tr key={`${row.Item_No}-${row.BatchCode || 'batch'}-${index}`} className="hover:bg-slate-50">
                          <td className="whitespace-nowrap px-3 py-2.5 text-slate-700">{startIndex + index + 1}</td>
                          <td className="whitespace-nowrap px-3 py-2.5 text-slate-700">{row.Item_Name || '-'}</td>
                          <td className="whitespace-nowrap px-3 py-2.5 text-slate-700">{row.BatchCode || '-'}</td>
                          <td className="whitespace-nowrap px-3 py-2.5 text-slate-700">{formatExpiryMonthYear(row.ExpiryDate)}</td>
                          <td className="whitespace-nowrap px-3 py-2.5 font-medium text-slate-800">{toNumber(row.Onhand).toLocaleString()}</td>
                          <td className="whitespace-nowrap px-3 py-2.5 text-slate-700">{formatDateDisplay(row.PurchaseDate)}</td>
                          <td className="whitespace-nowrap px-3 py-2.5 text-slate-700">{row.PurchaseRefNo || '-'}</td>
                          <td className="whitespace-nowrap px-3 py-2.5 text-slate-700">{row.SupplierName || '-'}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={9} className="px-4 py-10 text-center text-slate-500">
                          No expiry report data found for selected date.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </main>
      </div>
    </div>
  );
}

export default withAuth(ExpiryReport);
